import React, { useRef } from 'react'
import type { FxId } from '@/stores/sequencerStore'
import { useMidiMappable } from '@/hooks/useMidiMappable'

/**
 * Koala-style "button-is-the-fader" FX pad. One vertical touch/drag
 * gesture both engages the FX and sets its amount. Release disengages
 * (unless LATCH-mode is on, in which case tap toggles). The visual fill
 * inside the button reflects the current amount in one of three ways:
 *
 *   unipolar — fill grows from the bottom. 0 = empty, 1 = full.
 *   bipolar — center tick; fill grows upward (amount > 0.5) or downward
 *             (amount < 0.5). Middle = neutral / bypass.
 *   binary — full fill on engaged, empty on released. No per-Y gesture.
 *
 * Pointer-capture pattern modelled on TrimMarker.tsx:83–104. The
 * element captures the pointer on down so subsequent moves stay
 * targeted at this button even when the user drags past the visual
 * bounds — matches how a physical slider behaves.
 */

export type FxMode = 'unipolar' | 'bipolar' | 'binary'

interface FxPerformButtonProps {
  fxId: FxId
  label: string
  hint: string
  mode: FxMode
  active: boolean
  amount: number
  /**
   * LATCH-mode flag. When true, pointer-up does NOT disengage — the
   * FX stays latched at the last amount and clicking toggles instead.
   * Transient perform-only state; not persisted. Mutually exclusive
   * with `holdMode` (the store guarantees only one is true).
   */
  latchMode: boolean
  /**
   * HOLD-mode flag (Koala-style). When true, pointer-down explicitly
   * engages the FX and sets its amount from the touch Y — pointer-up
   * does NOT disengage and repeated taps don't toggle off. Sharing a
   * pad between taps lets the user "re-fader" an already-engaged FX
   * by tapping at a new Y.
   */
  holdMode: boolean
  onEngage: (fxId: FxId, active: boolean) => void
  onAmountChange: (fxId: FxId, amount: number) => void
  onResume: () => Promise<AudioContext | null>
}

export const FxPerformButton: React.FC<FxPerformButtonProps> = ({
  fxId,
  label,
  hint,
  mode,
  active,
  amount,
  latchMode,
  holdMode,
  onEngage,
  onAmountChange,
  onResume,
}) => {
  const dragRef = useRef<{ pointerId: number } | null>(null)

  // MIDI mapping — bipolar/unipolar FX expose amount as a continuous
  // control; binary FX (tape stop) expose engage as a toggle. Setting
  // a non-zero amount via MIDI auto-engages the FX so a user mapping
  // a knob to "reverb amount" gets the intuitive behaviour (twisting
  // the knob plays audible reverb) without a separate engage mapping.
  const fxMap = useMidiMappable<HTMLButtonElement>(
    mode === 'binary'
      ? {
          id: `fx.${fxId}`,
          kind: 'toggle',
          label: `FX ${label}`,
          setValue: v => onEngage(fxId, Boolean(v)),
        }
      : {
          id: `fx.${fxId}`,
          kind: 'continuous',
          label: `FX ${label}`,
          min: 0,
          max: 1,
          setValue: v => {
            if (typeof v !== 'number') return
            const clamped = Math.max(0, Math.min(1, v))
            onAmountChange(fxId, clamped)
            // Auto-engage threshold — tiny enough to count as "off"
            // for bipolar filter's 0.5 center, generous enough to
            // avoid engaging on MIDI noise.
            const engageThreshold = mode === 'bipolar' ? 0.02 : 0.02
            const neutral = mode === 'bipolar' ? 0.5 : 0
            const distanceFromNeutral = Math.abs(clamped - neutral)
            const shouldEngage = distanceFromNeutral > engageThreshold
            if (shouldEngage !== active) onEngage(fxId, shouldEngage)
          },
        },
  )

  const updateAmountFromPointer = (clientY: number, rect: DOMRect): number => {
    // Y=0 is top of button (= amount 1); Y=rect.height is bottom (= 0).
    const relY = clientY - rect.top
    const t = 1 - relY / rect.height
    return Math.max(0, Math.min(1, t))
  }

  const handlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    // Learn mode consumes the gesture — clicking the fader arms it
    // for MIDI capture instead of starting a drag.
    if (fxMap.armForLearn(e)) return
    e.preventDefault()
    void onResume()
    e.currentTarget.setPointerCapture(e.pointerId)
    dragRef.current = { pointerId: e.pointerId }

    if (mode === 'binary') {
      // Binary effects ignore amount entirely.
      //   HOLD-mode  — always ON, never toggles off.
      //   LATCH-mode — tap to toggle engage state.
      //   Momentary  — engage on down, release on up.
      if (holdMode) {
        onEngage(fxId, true)
      } else if (latchMode) {
        onEngage(fxId, !active)
      } else {
        onEngage(fxId, true)
      }
      return
    }

    const rect = e.currentTarget.getBoundingClientRect()
    const nextAmount = updateAmountFromPointer(e.clientY, rect)
    onAmountChange(fxId, nextAmount)

    if (holdMode) {
      // HOLD: engage if not already on; never toggle off. Tapping
      // an already-engaged pad re-faders the amount from touch Y
      // (set via `onAmountChange` above) — matches Koala's Hold
      // pad behaviour.
      if (!active) onEngage(fxId, true)
    } else if (latchMode) {
      // LATCH: toggle engage state on each touch.
      onEngage(fxId, !active)
    } else {
      // Momentary: engage now, release on pointer-up.
      onEngage(fxId, true)
    }
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== e.pointerId) return
    if (mode === 'binary') return
    const rect = e.currentTarget.getBoundingClientRect()
    const nextAmount = updateAmountFromPointer(e.clientY, rect)
    onAmountChange(fxId, nextAmount)
  }

  const handlePointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== e.pointerId) return
    dragRef.current = null
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
    // LATCH and HOLD both skip the pointer-up release. LATCH is
    // "sticky toggle" — turn off by tapping again. HOLD is
    // "on-only" — explicit on, no way to toggle off via tap.
    if (latchMode || holdMode) return
    // Momentary: release the FX.
    if (mode === 'binary') {
      onEngage(fxId, false)
      return
    }
    onEngage(fxId, false)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    // Keyboard fallback: space/enter engages. With HOLD on we only
    // turn on (never off); otherwise behaves as a toggle since
    // there's no natural "momentary" for a discrete keypress.
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault()
      void onResume()
      onEngage(fxId, holdMode ? true : !active)
    }
  }

  // Visual fill geometry — a positioned div inside the button whose
  // height (and vertical origin for bipolar) reflects `amount`.
  const fillStyle = computeFillStyle(mode, amount, active)

  return (
    <button
      type="button"
      ref={fxMap.ref}
      aria-pressed={active}
      aria-label={`${label} — ${hint}`}
      title={hint}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onKeyDown={handleKeyDown}
      className={`relative overflow-hidden h-full w-full rounded-lg border text-[11px] font-mono uppercase tracking-wider transition-colors select-none touch-none ${
        active
          ? 'border-accent shadow-[0_0_12px_var(--accent-halo)]'
          : 'border-brd-subtle hover:border-[color:var(--accent-halo)]'
      }`}
    >
      {/* Fill layer — behind the label. Inactive FX still show their
          current fader amount as a dimmed accent-colored fill so users
          can see "what would happen if I engage this" without having to
          tap first. When engaged the fill goes to full-brightness accent.
          Binary FX (tapeStop) render nothing when inactive since there's
          no amount to visualise. */}
      <div
        aria-hidden
        className={`absolute inset-x-0 transition-colors ${
          active ? 'bg-accent/80' : 'bg-accent/35'
        }`}
        style={fillStyle}
      />
      {/* Bipolar midpoint tick — only visible for bipolar FX. */}
      {mode === 'bipolar' && (
        <div
          aria-hidden
          className="absolute inset-x-1 top-1/2 h-px bg-content-tertiary/60 pointer-events-none"
        />
      )}
      {/* Numeric readout — bipolar shows signed (−100..+100 with 0 at
          center); unipolar shows 0..100. Binary FX skip the readout
          since they have no amount. Positioned top-right so it doesn't
          fight the centered label text. */}
      {mode !== 'binary' && (
        <span
          aria-hidden
          className={`absolute top-1 right-1.5 z-10 text-[9px] font-mono tabular-nums pointer-events-none ${
            active
              ? 'text-[color:var(--accent-ink)]/70'
              : 'text-content-tertiary'
          }`}
        >
          {formatFxAmount(mode, amount)}
        </span>
      )}
      {/* Label */}
      <span
        className={`relative z-10 flex items-center justify-center h-full px-1 text-center ${
          active ? 'text-[color:var(--accent-ink)]' : 'text-content-secondary'
        }`}
      >
        {label}
      </span>
    </button>
  )
}

/**
 * Format the fader amount for on-button display. Bipolar filter shows
 * a signed percentage around 0 (center = bypass); unipolar shows a
 * straight percentage. Keeps values tight (no decimals) so they fit in
 * the top-right corner of a ~130px-wide pad.
 */
function formatFxAmount(mode: FxMode, amount: number): string {
  if (mode === 'bipolar') {
    const pct = Math.round((amount - 0.5) * 200)
    if (pct === 0) return '0'
    return pct > 0 ? `+${pct}` : `${pct}`
  }
  return `${Math.round(amount * 100)}`
}

function computeFillStyle(
  mode: FxMode,
  amount: number,
  active: boolean,
): React.CSSProperties {
  if (mode === 'binary') {
    return active ? { top: 0, bottom: 0 } : { top: '100%', bottom: 0 }
  }
  if (mode === 'bipolar') {
    // Above midpoint: fill from center up.
    if (amount > 0.5) {
      const pct = (amount - 0.5) * 2 // 0..1
      return {
        top: `${50 - pct * 50}%`,
        bottom: '50%',
      }
    }
    // Below midpoint: fill from center down.
    const pct = (0.5 - amount) * 2 // 0..1
    return {
      top: '50%',
      bottom: `${50 - pct * 50}%`,
    }
  }
  // Unipolar — fill from bottom.
  const pct = Math.max(0, Math.min(1, amount)) * 100
  return {
    top: `${100 - pct}%`,
    bottom: 0,
  }
}
